import React, { useEffect, useState } from 'react';
import '../../../styles/components/Dashboard.scss';
import '../../../styles/components/SentimentCharts.scss';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
} from 'chart.js';
import { supabase } from '../../../helpers/Supabase';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

const SentimentCharts: React.FC = () => {
  const [chartData, setChartData] = useState<{
    pie: ChartData<'pie'> | null;
    line: ChartData<'line'> | null;
    bar: ChartData<'bar'> | null;
  }>({
    pie: null,
    line: null,
    bar: null,
  });

  useEffect(() => {
    const fetchSentimentData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userRow } = await supabase
        .from('users')
        .select('client_id')
        .eq('auth_user_id', user.id)
        .single();

      const { data: stores } = await supabase
        .from('stores')
        .select('id, store_name')
        .eq('client_id', userRow?.client_id);

      const storeIds = stores?.map((s) => s.id) || [];

      const { data: surveys } = await supabase
        .from('surveys')
        .select('created_at, sentiment, store_id')
        .in('store_id', storeIds);

      if (!surveys) return;

      // 🟢 Pie Chart
      const pieCounts = { positive: 0, neutral: 0, negative: 0 };
      surveys.forEach(({ sentiment }) => {
        if (sentiment in pieCounts) pieCounts[sentiment as keyof typeof pieCounts]++;
      });

      const pie: ChartData<'pie'> = {
        labels: ['Positive', 'Neutral', 'Negative'],
        datasets: [
          {
            data: [pieCounts.positive, pieCounts.neutral, pieCounts.negative],
            backgroundColor: ['#3dd57d', '#FFCA28', '#FF6B35'],
            hoverBackgroundColor: ['#26b66e', '#f4b400', '#E55A2B'],
            borderColor: '#222',
            borderWidth: 2,
          },
        ],
      };

      // 📈 Line Chart
      const groupedByDate: Record<string, Record<'positive' | 'neutral' | 'negative', number>> = {};
      surveys.forEach(({ created_at, sentiment }) => {
        const date = new Date(created_at).toLocaleDateString();
        if (!groupedByDate[date]) groupedByDate[date] = { positive: 0, neutral: 0, negative: 0 };
        if (sentiment in groupedByDate[date]) groupedByDate[date][sentiment as keyof typeof groupedByDate[string]]++;
      });

      const line: ChartData<'line'> = {
        labels: Object.keys(groupedByDate),
        datasets: [
          {
            label: 'Positive',
            data: Object.values(groupedByDate).map((d) => d.positive),
            borderColor: '#3dd57d',
            backgroundColor: 'rgba(61, 213, 125, 0.2)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
          },
          {
            label: 'Neutral',
            data: Object.values(groupedByDate).map((d) => d.neutral),
            borderColor: '#FFCA28',
            backgroundColor: 'rgba(255, 202, 40, 0.2)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
          },
          {
            label: 'Negative',
            data: Object.values(groupedByDate).map((d) => d.negative),
            borderColor: '#FF6B35',
            backgroundColor: 'rgba(255, 107, 53, 0.2)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
          },
        ],
      };

      // 📊 Bar Chart
      const byStore: Record<string, number> = {};
      surveys.forEach(({ store_id, sentiment }) => {
        if (sentiment === 'positive') {
          byStore[store_id] = (byStore[store_id] || 0) + 1;
        }
      });

      const bar: ChartData<'bar'> = {
        labels: stores?.map((s) => s.store_name) || [],
        datasets: [
          {
            label: 'Positive Reviews',
            data: stores?.map((s) => byStore[s.id] || 0) || [],
            backgroundColor: '#3dd57d',
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      };

      setChartData({ pie, line, bar });
    };

    fetchSentimentData();
  }, []);

  if (!chartData.pie || !chartData.line || !chartData.bar)
    return <p style={{ color: '#ccc', textAlign: 'center' }}>Loading charts...</p>;

  return (
    <div className="chart-section">
      <h2>Performance Insights</h2>
      <div className="chart-grid">
        <div className="chart-box">
          <h3>Sentiment Over Time</h3>
          <Line data={chartData.line} options={{
            plugins: {
              legend: {
                labels: { color: '#ccc', font: { size: 14 } }
              }
            },
            scales: {
              x: { ticks: { color: '#aaa' }, grid: { color: 'rgba(255,255,255,0.05)' }},
              y: { ticks: { color: '#aaa' }, grid: { color: 'rgba(255,255,255,0.05)' }}
            }
          }} />
        </div>
        <div className="chart-box">
          <h3>Store Positivity</h3>
          <Bar data={chartData.bar} options={{
            plugins: {
              legend: { display: false }
            },
            scales: {
              x: { ticks: { color: '#aaa' }, grid: { display: false }},
              y: { ticks: { color: '#aaa' }, grid: { color: 'rgba(255,255,255,0.05)' }}
            }
          }} />
        </div>
      </div>
    </div>
  );
};

export default SentimentCharts;
